/**
 * WeatherInputModal.js
 *
 * Big-button weather input modal for maritime conditions.
 * Designed for glove operation with 56px minimum tap targets.
 * Color-coded severity indicators for quick recognition.
 *
 * FT-284: now hosts <InlineTimeField> above the action row. Submit passes
 * (weatherData, timeOptions) up to the parent. On boundary error the modal
 * stays open, renders <ModalErrorBanner>, and lets the Master re-pick a time
 * with an override reason without dismissing the modal.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Cloud, Wind, Eye, Waves, Navigation, ChevronLeft } from 'lucide-react';
import { useInlineEventTime } from './useInlineEventTime';
import InlineTimeField from './InlineTimeField';
import ModalErrorBanner from './ModalErrorBanner';

// Wind speed ranges (knots)
const WIND_SPEEDS = [
  { value: 'calm', label: 'Calm', range: '0-5', knots: 0, color: 'green' },
  { value: 'light', label: 'Light', range: '6-15', knots: 10, color: 'green' },
  { value: 'moderate', label: 'Mod', range: '16-25', knots: 20, color: 'cyan' },
  { value: 'fresh', label: 'Fresh', range: '26-35', knots: 30, color: 'amber' },
  { value: 'strong', label: 'Strong', range: '36+', knots: 40, color: 'red' }
];

// Sea state (Beaufort scale)
const SEA_STATES = [
  { value: 'calm', label: 'Calm', beaufort: '0-1', color: 'green' },
  { value: 'slight', label: 'Slight', beaufort: '2-3', color: 'green' },
  { value: 'moderate', label: 'Mod', beaufort: '4', color: 'cyan' },
  { value: 'rough', label: 'Rough', beaufort: '5-6', color: 'amber' },
  { value: 'very_rough', label: 'V.Rough', beaufort: '7+', color: 'red' }
];

// Visibility ranges
const VISIBILITY = [
  { value: 'good', label: 'Good', range: '>5nm', color: 'green' },
  { value: 'moderate', label: 'Moderate', range: '2-5nm', color: 'cyan' },
  { value: 'poor', label: 'Poor', range: '0.5-2nm', color: 'amber' },
  { value: 'fog', label: 'Fog', range: '<0.5nm', color: 'red' }
];

// Wind directions for compass rose
const COMPASS_ROSE = [
  ['NW', 'N', 'NE'],
  ['W', 'VAR', 'E'],
  ['SW', 'S', 'SE']
];

// Color utility
const getButtonColor = (colorName, isSelected) => {
  const colors = {
    green: isSelected
      ? 'bg-green-600 border-green-700 ring-4 ring-green-300'
      : 'bg-green-500 hover:bg-green-600 border-green-600',
    cyan: isSelected
      ? 'bg-cyan-600 border-cyan-700 ring-4 ring-cyan-300'
      : 'bg-cyan-500 hover:bg-cyan-600 border-cyan-600',
    amber: isSelected
      ? 'bg-amber-600 border-amber-700 ring-4 ring-amber-300'
      : 'bg-amber-500 hover:bg-amber-600 border-amber-600',
    red: isSelected
      ? 'bg-red-600 border-red-700 ring-4 ring-red-300'
      : 'bg-red-500 hover:bg-red-600 border-red-600',
    slate: isSelected
      ? 'bg-slate-700 border-slate-700 ring-4 ring-slate-300'
      : 'bg-slate-400 hover:bg-slate-500 border-slate-500'
  };
  return colors[colorName] || colors.slate;
};

const WeatherInputModal = ({ isOpen, onClose, onSubmit, vesselTimezone }) => {
  // FT-284: UNCONDITIONAL hook calls — Rules-of-Hooks safe.
  const timeField = useInlineEventTime(vesselTimezone);

  const [windDirection, setWindDirection] = useState('');
  const [windSpeed, setWindSpeed] = useState(null);
  const [exactWindSpeed, setExactWindSpeed] = useState('');
  const [showExactWind, setShowExactWind] = useState(false);
  const [seaState, setSeaState] = useState('');
  const [visibility, setVisibility] = useState('');
  const [notes, setNotes] = useState('');

  // FT-284: boundary error + submit-in-flight guard.
  const [boundaryError, setBoundaryError] = useState(null);
  const submitInProgressRef = useRef(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setWindDirection('');
      setWindSpeed(null);
      setExactWindSpeed('');
      setShowExactWind(false);
      setSeaState('');
      setVisibility('');
      setNotes('');
      setBoundaryError(null);
      timeField.reset();
    }
    // timeField.reset is a stable useCallback; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const finalWindSpeed = showExactWind && exactWindSpeed
      ? parseInt(exactWindSpeed, 10)
      : windSpeed?.knots || null;

    if (!windDirection && !finalWindSpeed && !seaState && !visibility && !notes) {
      return;
    }

    if (!timeField.isValid || submitInProgressRef.current) return;
    submitInProgressRef.current = true;
    setBoundaryError(null);
    try {
      const timeOptions = timeField.freshSnapshot();
      const result = await onSubmit(
        {
          windDirection: windDirection || null,
          windSpeed: finalWindSpeed,
          seaState: seaState || null,
          visibility: visibility || null,
          notes: notes.trim() || null
        },
        timeOptions
      );
      if (result?.boundaryError) {
        setBoundaryError(result.boundaryError);
        return;
      }
      onClose();
    } finally {
      submitInProgressRef.current = false;
    }
  };

  const hasData = windDirection || windSpeed || exactWindSpeed || seaState || visibility || notes;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500 rounded-lg">
              <Cloud className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Log Weather</h2>
              <p className="text-sm text-slate-300">Current conditions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="p-4 overflow-y-auto flex-1 space-y-6">

          {/* Wind Direction - Compass Rose */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Navigation className="h-4 w-4 text-slate-500" />
              <label className="text-sm font-medium text-slate-700">Wind Direction</label>
            </div>

            {/* Compass Rose Grid */}
            <div className="grid grid-cols-3 gap-2 max-w-[200px] mx-auto mb-2">
              {COMPASS_ROSE.flat().map((dir) => {
                const isCenter = dir === 'VAR';
                const isCardinal = ['N', 'S', 'E', 'W'].includes(dir);
                const displayDir = dir === 'VAR' ? 'Variable' : dir;
                return (
                  <button
                    key={dir}
                    onClick={() => setWindDirection(displayDir)}
                    className={`
                      min-h-[56px] rounded-lg border-2 transition-all
                      flex flex-col items-center justify-center
                      ${windDirection === displayDir
                        ? 'bg-cyan-600 border-cyan-700 ring-4 ring-cyan-300 text-white'
                        : isCenter
                          ? 'bg-slate-200 border-slate-300 text-slate-600 hover:bg-slate-300'
                          : isCardinal
                            ? 'bg-cyan-100 border-cyan-200 text-cyan-700 hover:bg-cyan-200'
                            : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                      }
                    `}
                  >
                    <span className="text-lg font-bold">{dir === 'VAR' ? 'Var' : dir}</span>
                  </button>
                );
              })}
            </div>

            {/* Calm button */}
            <button
              onClick={() => setWindDirection('Calm')}
              className={`
                w-full max-w-[200px] mx-auto block min-h-[48px] rounded-lg border-2 transition-all
                ${windDirection === 'Calm'
                  ? 'bg-green-600 border-green-700 ring-4 ring-green-300 text-white'
                  : 'bg-green-100 border-green-200 text-green-700 hover:bg-green-200'
                }
              `}
            >
              <span className="font-medium">Calm</span>
            </button>
          </div>

          {/* Wind Speed */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Wind className="h-4 w-4 text-slate-500" />
              <label className="text-sm font-medium text-slate-700">Wind Speed</label>
            </div>

            {!showExactWind ? (
              <>
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {WIND_SPEEDS.map((speed) => (
                    <button
                      key={speed.value}
                      onClick={() => { setWindSpeed(speed); setExactWindSpeed(''); }}
                      className={`
                        min-h-[56px] rounded-lg border-2 transition-all
                        flex flex-col items-center justify-center text-white
                        ${getButtonColor(speed.color, windSpeed?.value === speed.value)}
                      `}
                    >
                      <span className="text-sm font-bold">{speed.label}</span>
                      <span className="text-xs opacity-80">{speed.range}kt</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setShowExactWind(true); setWindSpeed(null); }}
                  className="w-full px-4 py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Enter exact knots
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={exactWindSpeed}
                    onChange={(e) => setExactWindSpeed(e.target.value)}
                    placeholder="Enter knots"
                    min="0"
                    max="100"
                    className="flex-1 px-4 py-3 min-h-[56px] border border-slate-300 rounded-lg text-lg text-center"
                    autoFocus
                  />
                  <span className="flex items-center text-slate-500 font-medium">kt</span>
                </div>
                <button
                  onClick={() => { setShowExactWind(false); setExactWindSpeed(''); }}
                  className="w-full flex items-center justify-center gap-1 px-4 py-2 text-sm text-slate-500"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back to quick select
                </button>
              </div>
            )}
          </div>

          {/* Sea State */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Waves className="h-4 w-4 text-slate-500" />
              <label className="text-sm font-medium text-slate-700">Sea State</label>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {SEA_STATES.map((state) => (
                <button
                  key={state.value}
                  onClick={() => setSeaState(state.value)}
                  className={`
                    min-h-[56px] rounded-lg border-2 transition-all
                    flex flex-col items-center justify-center text-white
                    ${getButtonColor(state.color, seaState === state.value)}
                  `}
                >
                  <span className="text-sm font-bold">{state.label}</span>
                  <span className="text-xs opacity-80">BF {state.beaufort}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Visibility */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-4 w-4 text-slate-500" />
              <label className="text-sm font-medium text-slate-700">Visibility</label>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {VISIBILITY.map((vis) => (
                <button
                  key={vis.value}
                  onClick={() => setVisibility(vis.value)}
                  className={`
                    min-h-[56px] rounded-lg border-2 transition-all
                    flex flex-col items-center justify-center text-white
                    ${getButtonColor(vis.color, visibility === vis.value)}
                  `}
                >
                  <span className="text-sm font-bold">{vis.label}</span>
                  <span className="text-xs opacity-80">{vis.range}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional weather observations..."
              rows={2}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none text-base"
            />
          </div>
        </div>

        {/* FT-283 §G: inline time field + boundary banner sit just above the
            action row so they remain visible after the user scrolls through
            the long content above. */}
        <div className="px-4 pt-3 pb-1 space-y-3 bg-white">
          {boundaryError && (
            <ModalErrorBanner message={boundaryError.humanMessage} />
          )}
          <InlineTimeField
            timeField={timeField}
            vesselTimezone={vesselTimezone}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!hasData || !timeField.isValid}
            className="flex-1 py-3 px-4 min-h-[56px] bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Log Weather
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeatherInputModal;
